<template>
  <span v-if="!conid" class="text-muted-color">–</span>
  <Skeleton v-else-if="showInitialSkeleton" width="3rem" height="1rem" animation="none" />
  <span v-else-if="displayValue !== null && format === 'percent'">{{ displayValue }}</span>
  <span v-else-if="displayValue !== null" :class="colorClass">
    <template v-if="format === 'dollars'">${{ displayValue }}</template>
    <template v-else>{{ displayValue }}&cent;</template>
  </span>
  <span v-else class="text-muted-color">–</span>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef, watch } from 'vue';
import { useGetEventContractBidAsk, useGetFlexbestBidAsk } from '@/api/mdfarm.queries';
import { useFlexbestBidAskStream } from '@/composables/useFlexbestBidAskStream';
import { useUserState } from '@/stores/useUserState';
import { usePriceSideSetting } from '@/composables/usePriceSideSetting';
import { formatDollarPrice, formatPercentPrice } from '@/utils/formatPrice';

const props = withDefaults(
  defineProps<{
    conid?: number;
    conidExPairs?: string[];
    exchange: string;
    side?: 'bid' | 'ask';
    sentiment?: 'positive' | 'negative';
    enabled?: boolean;
    format?: 'cents' | 'dollars' | 'percent';
    /** Seed value (0-1) shown immediately while REST loads */
    seed?: number;
    /** When true, non-auth mode uses seed only (no REST). Auth mode uses WS. */
    seedMode?: boolean;
  }>(),
  { enabled: true, format: 'dollars' },
);

const { priceSide: defaultPriceSide } = usePriceSideSetting();
const resolvedSide = computed(() => props.side ?? defaultPriceSide.value);
const userState = useUserState();

const wasEnabled = shallowRef(props.enabled);
watch(
  () => props.enabled,
  (val) => {
    if (val) wasEnabled.value = true;
  },
);

// seedMode=true + auth: WS
// seedMode=true + non-auth + has seed: seed only (no network)
// seedMode=true + non-auth + no seed: REST fallback
// seedMode=false (default): always REST
const hasSeed = computed(() => props.seed !== undefined && props.seed !== null);
const useWsMode = computed(() => !!props.seedMode && !userState.usePublicApi);
const useRestMode = computed(() => !props.seedMode || (!!props.seedMode && userState.usePublicApi && !hasSeed.value));

// ── WS mode (auth + seed) ──
const wsConidExPairs = computed(() => {
  if (!useWsMode.value || !wasEnabled.value) return [];
  if (props.conidExPairs && props.conidExPairs.length > 1) return props.conidExPairs;
  if (props.conid) return [`${props.conid}@${props.exchange}`];
  return [];
});

const stream = useFlexbestBidAskStream(wsConidExPairs, {
  seedBid: () => props.seed,
  seedAsk: () => props.seed,
  isLive: () => wasEnabled.value,
});

// ── REST mode (no seed — Closing Soon, etc.) ──
const useFlexbest = computed(() => useRestMode.value && wasEnabled.value && (props.conidExPairs?.length ?? 0) > 1);

const singleQuery = useGetEventContractBidAsk(
  computed(() => (useRestMode.value && !useFlexbest.value && wasEnabled.value ? props.conid : undefined)),
  computed(() => props.exchange),
);
const flexbestQuery = useGetFlexbestBidAsk(computed(() => (useFlexbest.value ? props.conidExPairs : undefined)));

const restData = computed(() => (useFlexbest.value ? flexbestQuery.data.value : singleQuery.data.value));
const isFetching = computed(() =>
  useRestMode.value ? (useFlexbest.value ? flexbestQuery.isFetching.value : singleQuery.isFetching.value) : false,
);

const settled = ref(false);
watch(isFetching, (val) => {
  if (!val) settled.value = true;
});
const showInitialSkeleton = computed(() => useRestMode.value && isFetching.value && !settled.value);

const colorClass = computed(() => {
  if (props.sentiment) return props.sentiment === 'positive' ? 'text-positive' : 'text-negative';
  return resolvedSide.value === 'bid' ? 'text-positive' : 'text-negative';
});

const displayValue = computed(() => {
  let raw: number | null | undefined = null;

  if (useWsMode.value) {
    raw = resolvedSide.value === 'bid' ? stream.bid.value : stream.ask.value;
  } else if (useRestMode.value) {
    raw = restData.value ? (resolvedSide.value === 'bid' ? restData.value.bid : restData.value.ask) : null;
  } else {
    // Non-auth + seed: just show seed
    raw = props.seed ?? null;
  }

  if (raw === null || raw === undefined) return null;
  if (props.format === 'percent') return formatPercentPrice(raw);
  const price = formatDollarPrice(raw);
  return price === '—' ? null : price;
});
</script>
<!-- last modified by AI tools on 2026-10-12 -->
